import React, { PropsWithChildren } from 'react';
import {
  QueryClient,
  QueryClientProvider as BaseQueryClientProvider,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import wait from './wait';

const MAX_RETRIES = 3;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = isAxiosError(error) ? error.response?.status : undefined;

        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }

        return failureCount < MAX_RETRIES;
      },
    },
  },
});

const REFETCH_DELAYS_MS = [1000, 3000, 6000];

// Invalidates (rather than only refetching active) so queries with a
// staleTime that are not mounted right now won't serve pre-mutation data.
export function refetchActiveQueries() {
  return queryClient.invalidateQueries();
}

export async function refetchActiveQueriesDelayed(
  onStep?: () => void | Promise<void>,
) {
  for (const delay of REFETCH_DELAYS_MS) {
    await wait(delay);
    await onStep?.();
    await refetchActiveQueries();
  }
}

const QueryClientProvider = ({ children }: PropsWithChildren) => (
  <BaseQueryClientProvider client={queryClient}>
    {children}
  </BaseQueryClientProvider>
);

export default QueryClientProvider;
